import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { EventTypeReady } from '@core/customEvent/eventTypeReady'
import { PostmessageSentinel } from '@core/customEvent/postMessageWrapper'
import { useContextTranslation } from '@core/i18n'
import { useSessionStorage } from '@core/storage/hooks'

import { enterPIP, exitPIP } from './controls'
import { isInPIPIframe } from './isInPIPIframe'
import { ABLE_TO_IFRAMEIZE_KEY } from './keys'
import { setWindowHref } from './setWindowHref'
import type { PIPParams } from './types'

interface Props {
  enabled: boolean | null | undefined // enables/disables PIP
  params?: PIPParams
  containerRef?: RefObject<HTMLElement | null> // container rendering the player, kept interactive behind the PIP iframe
}

interface Return {
  isIframized: boolean
  isAbleToIframize: boolean
  latestHref: string
  enter: (href?: string) => void
  exit: () => void
}

/**
 *  Hook providing the Picture-in-Picture (PIP) controls and state.
 */
export const usePIPControls = (props: Props): Return => {
  const { pip_observe: observeDOM, pip_outbound_target: outboundTarget } =
    props.params || {}
  const enabled = props.enabled && !isInPIPIframe()
  const { containerRef } = props

  const { t } = useContextTranslation()

  const [isAbleToIframize, setIsAbleToIframize] = useSessionStorage(
    ABLE_TO_IFRAMEIZE_KEY,
    true,
  )
  const [isIframized, setIsIframized] = useState(false)

  const desiredHrefRef = useRef<string | undefined>(undefined)
  const [latestHref, setLatestHref] = useState(window.location.href)

  const onLocationChange = useCallback((href: string) => {
    setLatestHref(href)
  }, [])

  /**
   * Set the flag to indicate successful PIP iframe load.
   * Note: `load` event on iframe is not reliable since many
   * pages can be in a loading state for a very long time.
   * See `message` event listener below.
   */
  const onLoad = useCallback(() => {
    setIsIframized(true)
  }, [])

  /**
   * Handle error from PIP controller.
   */
  const onError = useCallback(() => {
    setIsIframized((wasIframized) => {
      if (wasIframized === false) {
        setIsAbleToIframize(false)

        // If we were not able to iframeize,
        // we need to restore the desired href.
        if (desiredHrefRef.current) {
          setWindowHref(desiredHrefRef.current)
        }
      }
      return false
    })
  }, [setIsAbleToIframize])

  const enter = useCallback(
    (href?: string) => {
      desiredHrefRef.current = href
      const src = href || window.location.href
      const scrollY = href !== window.location.href ? 0 : window.scrollY
      if (isAbleToIframize && enabled) {
        enterPIP({
          onError,
          onLoad,
          onLocationChange,
          options: { observeDOM, outboundTarget },
          preserve: containerRef?.current,
          scrollY,
          src,
          title: t('player:Picture in Picture frame'),
        })
      } else if (src !== window.location.href) {
        setWindowHref(src)
      }
    },
    [
      isAbleToIframize,
      enabled,
      containerRef,
      onError,
      onLoad,
      onLocationChange,
      observeDOM,
      outboundTarget,
      t,
    ],
  )

  const exit = useCallback(() => {
    exitPIP()
  }, [])

  /**
   * Because `load` event on PIP iframe can take a long time to fire,
   * we listen to FW script from PIP iframe to fire "pip ready" message
   * to its parent to indicate a successful load.
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        event.data.sentinel === PostmessageSentinel.INTERNAL &&
        event.data.action === EventTypeReady.FW_PIP_READY
      ) {
        setIsIframized(true)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return {
    isIframized,
    isAbleToIframize,
    latestHref,
    enter,
    exit,
  }
}
